import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardActions, CardContent, CardMedia, Typography, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../services/productService';

/**
 * Reusable ProductCard component
 * 
 * This component displays a product in a consistent card format across the application.
 * It uses the preferred styling from the main Products page.
 * 
 * @param {Object} product - The product object to display
 * @param {Object} sx - Optional additional styles to apply to the Card component
 */
const ProductCard = ({ product, sx = {} }) => {
  const navigate = useNavigate();
  
  if (!product) return null;
  
  const handleCardClick = () => {
    navigate(`/products/${product.slug || product.id}`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
        boxShadow: 3,
        '&:hover': { 
          boxShadow: 6,
          cursor: 'pointer' 
        },
        ...sx
      }}
      onClick={handleCardClick}
    >
      <CardMedia
        component="img"
        image={
          product.images?.[0]?.url ||
          product.thumbnail ||
          'https://via.placeholder.com/600x400?text=No+Image+Available'
        }
        alt={product.images?.[0]?.alt_text || product.name}
        sx={{
          width: '100%',
          height: 350,
          objectFit: 'cover',
          borderRadius: '4px 4px 0 0',
        }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          variant="h6"
          component="h2"
          gutterBottom
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '3em',
          }}
        >
          {/* Use meta title or product name */}
          {product.meta?.title || product.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {formatCurrency(product.price)}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mt: 1, 
            display: '-webkit-box', 
            WebkitBoxOrient: 'vertical', 
            WebkitLineClamp: 2, 
            overflow: 'hidden' 
          }}
        >
          {product.short_description || product.shortDescription || product.description}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'center', pb: 2 }} onClick={(e) => e.stopPropagation()}>
        <Button
          size="small"
          color="primary"
          variant='contained'
          component={Link}
          to={`/products/${product.slug || product.id}`}
        >
          View Details
        </Button>
        {product.has_configurator && (
          <Button
            size="small"
            color="secondary"
            variant='outlined'
            component={Link}
            to={`/configurator?design=${product.id}`}
            sx={{ ml: 1 }}
          >
            Customize
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    thumbnail: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      alt_text: PropTypes.string,
      is_primary: PropTypes.bool,
    })),
    shortDescription: PropTypes.string,
    hasConfigurator: PropTypes.bool,
    meta: PropTypes.shape({
      title: PropTypes.string
    })
  }).isRequired,
  sx: PropTypes.object
};

ProductCard.defaultProps = {
  sx: {}
};

export default ProductCard;
